import { ChangeDetectionStrategy, Component, Signal, computed, inject } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HD_BODYGRAPH_COLORS } from '../../bodygraph/hd-bodygraph-metadata';
import { HdBodygraphPlanetaryActivation } from '../../bodygraph/hd-bodygraph.models';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { getHdPlanetDisplayConfigByPlanetId } from '../../bodygraph/planet-grid/hd-planet-display-config';
import {
  HD_JUST_NOW_TRANSIT_BASE_COLOR,
  HD_JUST_NOW_TRANSIT_COLOR,
} from '../just-now-transit/hd-just-now-transit';
import { HdFixedStarsPanelService } from '../../fixed-stars/hd-fixed-stars-panel.service';
import {
  HdLocksKeysPeriodId,
  getHdLocksKeysLockMeta,
  hdLocksKeysKeyGate,
  hdLocksKeysPeriodIndex,
} from './hd-locks-keys-metadata';
import { hdLocksKeysKeyText, hdLocksKeysPeriodText } from './hd-locks-keys-period-text';
import { HdLocksKeysStarNoteSegment, parseHdLocksKeysStarNote } from './hd-locks-keys-star-note';
import { HdLocksKeysStateService } from './hd-locks-keys-state.service';

export interface HdLocksKeysLockDetailData {
  readonly gate: number;
}

export function hdLocksKeysLockDetailPanelKey(gate: number): string {
  return `locks-keys-lock:${gate}`;
}

/** Astronomicon-planeetglyph in het chart-statusblok, met eigenaars-/zijde-kleur. */
interface LockDetailPlanetGlyphVm {
  readonly glyph: string;
  readonly color: string;
}

interface LockDetailKeyVm {
  readonly periodId: HdLocksKeysPeriodId;
  readonly gate: number;
  readonly periodLabel: string;
  readonly title: string;
  readonly name: string;
  readonly circuit: string;
  readonly cross: string;
  readonly description: string;
  readonly planetaryNuance: string;
  /** Kanaal-synergie met andere sleutels van de periode (bv. 37/40, 20/34/57). */
  readonly channelNote: string;
  /** 2027-sternoot met klikbare sternamen (leeg voor keys zonder sterrol). */
  readonly starNote: readonly HdLocksKeysStarNoteSegment[];
  readonly isActivePeriod: boolean;
}

/**
 * Detailpaneel van één lock (spec 2026-08-27): kern, de oude en nieuwe sleutel
 * met circuit/kruis en hun werking in het slot, de verschuiving, mens/rave-
 * impact en de analistnotitie — de volledige samenvattingscontent uit de
 * `human-design/locks-keys`-scope. In de chart-scope toont de kop bovendien of
 * de chart de keys en de lock-poort zelf activeert.
 */
@Component({
  selector: 'hd-locks-keys-lock-detail',
  imports: [SharedModule],
  templateUrl: './hd-locks-keys-lock-detail.component.html',
  styleUrl: './hd-locks-keys-lock-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysLockDetailComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdLocksKeysLockDetailData>
    | null;
  protected readonly state = inject(HdLocksKeysStateService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly fixedStarsPanel = inject(HdFixedStarsPanelService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly gate = computed(() => this.panelData?.()?.gate ?? 0);

  protected readonly lockMeta = computed(() => getHdLocksKeysLockMeta(this.gate()));

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.gateInfo.locksKeysTextIn(key, this.lang(), params);
  }

  protected lockText(field: string): string {
    return this.t(`locks.${this.gate()}.${field}`);
  }

  protected readonly groupLabel = computed(() => {
    const meta = this.lockMeta();
    if (meta == null) {
      return '';
    }
    return this.t(meta.group === 'vessel' ? 'detail.groupVessel' : 'detail.groupSphinx');
  });

  protected readonly keys = computed<readonly LockDetailKeyVm[]>(() => {
    const meta = this.lockMeta();
    if (meta == null) {
      return [];
    }
    const activePeriodId = this.state.periodId();
    const lang = this.lang();
    // De twee periodes van het paar, chronologisch: de vroegste is de "oude", de latere de "nieuwe" sleutel.
    const pairPeriods = [...this.state.periodPair()]
      .sort((a, b) => hdLocksKeysPeriodIndex(a) - hdLocksKeysPeriodIndex(b));
    return pairPeriods.map((periodId, index) => {
      const text = hdLocksKeysKeyText(this.gateInfo, lang, meta.gate, periodId);
      return {
        periodId,
        gate: hdLocksKeysKeyGate(meta.gate, periodId),
        periodLabel: hdLocksKeysPeriodText(this.gateInfo, lang, periodId).label,
        title: this.t(index === 0 ? 'detail.oldKeyTitle' : 'detail.newKeyTitle'),
        name: text.name,
        circuit: text.circuit,
        cross: text.cross,
        description: text.description,
        planetaryNuance: text.planetaryNuance,
        channelNote: text.channelNote,
        starNote: parseHdLocksKeysStarNote(text.starNote2027),
        isActivePeriod: periodId === activePeriodId,
      };
    });
  });

  /** Opent het fixed-star-info-paneel voor een sternaam uit de 2027-noot. */
  protected openFixedStar(side: number, event: MouseEvent): void {
    this.fixedStarsPanel.openForStar(side, null, { x: event.clientX, y: event.clientY });
  }

  /**
   * Chart-status per key + de lock-poort zelf (enkel in de chart-scope). Op
   * een composite-tab telt de unie van beide charts en kleuren de
   * planeetglyphs per persoon (chart 1 blauw, chart 2 mint); anders per zijde.
   */
  protected readonly chartStatusLines = computed<
    readonly { key: string; text: string; activated: boolean; planetGlyphs: readonly LockDetailPlanetGlyphVm[] }[]
  >(() => {
    if (this.state.effectiveScope() !== 'chart') {
      return [];
    }
    const meta = this.lockMeta();
    if (meta == null) {
      return [];
    }
    const hasPartner = this.state.hasPartner();
    const sources: readonly {
      readonly activations: readonly HdBodygraphPlanetaryActivation[];
      readonly colorFor: (activation: HdBodygraphPlanetaryActivation) => string;
    }[] = hasPartner
      ? [
          { activations: this.state.chartPlanetaryActivations(), colorFor: () => HD_JUST_NOW_TRANSIT_BASE_COLOR },
          { activations: this.state.partnerPlanetaryActivations(), colorFor: () => HD_JUST_NOW_TRANSIT_COLOR },
        ]
      : [{
          activations: this.state.chartPlanetaryActivations(),
          colorFor: activation =>
            activation.side === 'design' ? HD_BODYGRAPH_COLORS.design : HD_BODYGRAPH_COLORS.personality,
        }];
    const planetGlyphsFor = (gate: number): readonly LockDetailPlanetGlyphVm[] =>
      sources.flatMap(source =>
        source.activations
          .filter(activation => activation.gate === gate)
          .map(activation => ({
            glyph: getHdPlanetDisplayConfigByPlanetId(activation.planetId)?.astronomiconChar ?? '',
            color: source.colorFor(activation),
          }))
          .filter(entry => entry.glyph.length > 0),
      );
    const activations = this.state.combinedActivations();
    const isActivated = (gate: number): boolean => activations.some(activation => activation.gate === gate);

    const lines: { key: string; text: string; activated: boolean; planetGlyphs: readonly LockDetailPlanetGlyphVm[] }[] = [];
    const lang = this.lang();
    for (const periodId of this.state.periodPair()) {
      const keyGate = hdLocksKeysKeyGate(meta.gate, periodId);
      const activated = isActivated(keyGate);
      const periodLabel = hdLocksKeysPeriodText(this.gateInfo, lang, periodId).label;
      lines.push({
        key: `key-${periodId}`,
        activated,
        planetGlyphs: activated ? planetGlyphsFor(keyGate) : [],
        text: activated
          ? `${periodLabel} — ${this.t('detail.keyActivatedBy', { gate: keyGate })}`
          : `${periodLabel} — ${this.t('detail.keyNotActivated', { gate: keyGate })}`,
      });
    }
    const lockActivated = isActivated(meta.gate);
    lines.push({
      key: 'lock',
      activated: lockActivated,
      planetGlyphs: lockActivated ? planetGlyphsFor(meta.gate) : [],
      text: lockActivated
        ? this.t('detail.lockActivatedBy', { gate: meta.gate })
        : this.t('detail.lockNotActivated', { gate: meta.gate }),
    });
    return lines;
  });
}
